import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApplicationApiUrls } from 'src/app/constants/api-url-constants';
import {
  Bookings,
  PropertyImageType,
  PropertyListing,
  PropertyStatusData,
  ToggleAvailability,
} from 'src/app/shared/models';
import { BaseInterface, ExtendedBaseInterface } from 'src/app/store/auth/auth.models';
import { environment } from 'src/environments/environment';
import { clearNullValues } from './util.service';
import { UserFilterWithPagination } from 'src/app/models/request-models/user-filter-with-pagination-request';
import { HttpParams } from '@angular/common/http';

export interface WishlistCreateRequest {
  id?: number;
  propertyProfileId: number;
  name?: string;
}
export interface WishlistResponseItem {
  id: number;
  propertyProfileId: number;
  name?: string;
}
export interface WishlistQuery {
  pageNumber?: number;
  pageSize?: number;
  sortColumn?: string;
  sortOrder?: string;
  search?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private http: HttpClient) {}

  getHomePageListings(data: any) {
    return this.http.post<ExtendedBaseInterface>(`${environment.apiUrl}${ApplicationApiUrls.GetListings}`, data);
  }

  getAllAmenities() {
    return this.http.get<BaseInterface>(`${environment.apiUrl}/Lookup/get-all-amenities`);
  }

  getAllPropertyTypes() {
    return this.http.get<BaseInterface>(`${environment.apiUrl}/Lookup/get-property-types`);
  }

  uploadFile(file: File, imageType: PropertyImageType) {
    const formData: FormData = new FormData();
    formData.append('File', file);
    formData.append('ImageType', imageType.toString());

    return this.http.post<BaseInterface>(
      `${environment.apiUrl}/${imageType === PropertyImageType.Blog ? 'Blog' : 'PropertyProfile'}/UploadImage`,
      formData,
    );
  }

  createUpdatePropertyListing(data: Partial<PropertyListing>, isDraft: boolean) {
    return this.http.post<BaseInterface>(
      `${environment.apiUrl}${ApplicationApiUrls.AddListing}?isDraft=${isDraft}`,
      data,
    );
  }

  getPropertyById(propertyId: number) {
    return this.http.get<BaseInterface>(
      `${environment.apiUrl}${ApplicationApiUrls.GetPropertyProfileById}${propertyId}`,
    );
  }

  setPropertyStatus(data: PropertyStatusData) {
    const params = {
      propertyStatus: data.propertyStatus,
      rejectReason: data.rejectReason || null,
    };

    return this.http.patch<BaseInterface>(
      `${environment.apiUrl}/PropertyProfile/${data.propertyId}/status`,
      clearNullValues(params),
    );
  }

  deleteProperty(propertyId: number) {
    return this.http.delete<BaseInterface>(`${environment.apiUrl}/PropertyProfile/DeleteProperty/${propertyId}`);
  }

  getAllUsers(data: Partial<UserFilterWithPagination>) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.apiUrl}/Admin/GetAllUser?isHost=${data.isHost}&pageNumber=${data.pageNumber}&pageSize=${data.pageSize}&SortColumn=${data.sortColumn}&SortOrder=${data.sortOrder}&Search=${data.search}`,
    );
  }

  getBookings(data: Bookings, url: ApplicationApiUrls) {
    let params = new URLSearchParams();
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined) {
        params.append(key, data[key].toString());
      }
    });

    return this.http.get<ExtendedBaseInterface>(`${environment.apiUrl}${url}?${params.toString()}`);
  }

  toggleAvailability(data: ToggleAvailability) {
    return this.http.patch<BaseInterface>(
      `${environment.apiUrl}/PropertyProfile/${data.propertyId}/availability?isAvailable=${data.isAvailable}`,
      '',
    );
  }
  // New Wishlist endpoints per Swagger
  createWishlistItem(dto: WishlistCreateRequest) {
    return this.http.post<BaseInterface>(`${environment.apiUrl}${ApplicationApiUrls.Wishlist}`, dto);
  }

  getWishlistPaged(filter: WishlistQuery = {}) {
    let params = new HttpParams();
    if (filter.pageNumber !== undefined) params = params.set('PageNumber', String(filter.pageNumber));
    if (filter.pageSize !== undefined) params = params.set('PageSize', String(filter.pageSize));
    if (filter.sortColumn) params = params.set('SortColumn', filter.sortColumn);
    if (filter.sortOrder) params = params.set('SortOrder', filter.sortOrder);
    if (filter.search) params = params.set('Search', filter.search);
    return this.http.get<ExtendedBaseInterface>(`${environment.apiUrl}${ApplicationApiUrls.Wishlist}`, { params });
  }

  getWishlistById(id: number) {
    return this.http.get<BaseInterface>(`${environment.apiUrl}${ApplicationApiUrls.Wishlist}/${id}`);
  }

  deleteWishlistItem(id: number) {
    return this.http.delete<BaseInterface>(`${environment.apiUrl}${ApplicationApiUrls.RemoveWishlistItem}`.replace('{listingId}', String(id)));
  }

  // Existing wishlist methods used by NgRx effects (backward compatibility)
  getWishlist(role?: 'guest' | 'host', locationKey?: string) {
    let params = new HttpParams();
    if (role) params = params.set('role', role);
    if (locationKey) params = params.set('location', locationKey);
    return this.http.get<ExtendedBaseInterface>(`${environment.apiUrl}${ApplicationApiUrls.Wishlist}`, { params });
  }

  addWishlistItem(propertyId: number) {
    const url = `${environment.apiUrl}${ApplicationApiUrls.AddWishlistItem}`.replace('{listingId}', String(propertyId));
    return this.http.post<BaseInterface>(url, {});
  }
}
